import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, ChevronRight, History } from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import UploadStep from "@/components/wizard/UploadStep";
import MappingStep from "@/components/wizard/MappingStep";
import ResultsStep from "@/components/wizard/ResultsStep";
import { useLang } from "@/context/LanguageContext";
import { fetchSchema } from "@/lib/api";
import { WIZARD } from "@/constants/testIds/wizard";
import { Button } from "@/components/ui/button";

const STEPS = ["upload", "mapping", "results"];
const STORAGE_KEY = "riclassificatore.session.v1";

const saveSession = (data) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
};
const readSession = () => {
  try { const s = localStorage.getItem(STORAGE_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
};
const clearSession = () => { try { localStorage.removeItem(STORAGE_KEY); } catch {} };

export default function Wizard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState("upload");
  const [rawItems, setRawItems] = useState([]);
  const [priorYears, setPriorYears] = useState([]); // [{label, items}]
  const [classifiedItems, setClassifiedItems] = useState([]);
  const [results, setResults] = useState(null);
  const [categories, setCategories] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [sector, setSector] = useState("generico");
  const [periodMonths, setPeriodMonths] = useState(12);
  const [showResume, setShowResume] = useState(false);

  useEffect(() => {
    fetchSchema().then((d) => {
      setCategories(d.categories || []);
      setSectors(d.sectors || []);
    }).catch(() => {});
    const s = readSession();
    if (s && s.step && s.step !== "upload") setShowResume(true);
    // fetchSchema/readSession are module-level imports — intentionally stable, not re-run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const restoreSession = () => {
    const s = readSession();
    if (!s) return;
    setStep(s.step);
    setRawItems(s.rawItems || []);
    setPriorYears(s.priorYears || []);
    setClassifiedItems(s.classifiedItems || []);
    setResults(s.results || null);
    setSector(s.sector || "generico");
    setPeriodMonths(s.periodMonths || 12);
    setShowResume(false);
    toast.success(t("resume_yes"));
  };

  const dismissResume = () => {
    clearSession();
    setShowResume(false);
  };

  const handleParsed = useCallback((items, prior = []) => {
    setRawItems(items);
    setPriorYears(prior);
    setStep("mapping");
    saveSession({ step: "mapping", rawItems: items, priorYears: prior, sector, periodMonths });
    toast.success(`${items.length} ${t("parsed_ok")}`);
  }, [t, sector, periodMonths]);

  const handleMappingConfirmed = useCallback((mapped, computed) => {
    setClassifiedItems(mapped);
    setResults(computed);
    setStep("results");
    saveSession({ step: "results", rawItems, priorYears, classifiedItems: mapped, results: computed, sector, periodMonths });
  }, [rawItems, priorYears, sector, periodMonths]);

  const restart = () => {
    clearSession();
    setRawItems([]);
    setPriorYears([]);
    setClassifiedItems([]);
    setResults(null);
    setStep("upload");
    navigate("/riclassifica");
  };

  const currentIdx = STEPS.indexOf(step);
  const labels = { upload: t("step_upload"), mapping: t("step_mapping"), results: t("step_results") };

  return (
    <div className="min-h-screen bg-slate-50" data-testid={WIZARD.root}>
      <Header />
      <div className="mx-auto max-w-7xl px-4 pb-20 pt-8 sm:px-6">
        {showResume && (
          <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm">
            <History className="h-5 w-5 shrink-0 text-blue-700" strokeWidth={1.75} />
            <div className="flex-1">
              <div className="font-medium text-slate-900">{t("resume_title")}</div>
              <div className="text-xs text-slate-600">{t("resume_desc")}</div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="wizard-resume-yes" size="sm" onClick={restoreSession} className="rounded-full bg-blue-700 text-white hover:bg-blue-800">{t("resume_yes")}</Button>
              <Button data-testid="wizard-resume-no" size="sm" variant="outline" onClick={dismissResume} className="rounded-full border-slate-300">{t("resume_no")}</Button>
            </div>
          </div>
        )}

        <nav
          data-testid={WIZARD.stepIndicator}
          className="mb-10 flex items-center gap-2 rounded-full border border-slate-200 bg-white p-2 text-sm shadow-sm"
        >
          {STEPS.map((s, i) => {
            const active = s === step;
            const done = i < currentIdx;
            return (
              <div key={s} className="flex flex-1 items-center gap-2">
                <div className={`flex flex-1 items-center gap-2 rounded-full px-4 py-2 font-medium transition-colors ${active ? "bg-slate-900 text-white" : done ? "text-slate-800" : "text-slate-400"}`}>
                  <span className={`grid h-6 w-6 place-items-center rounded-full text-xs ${active ? "bg-white text-slate-900" : done ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {done ? <CheckCircle2 className="h-4 w-4" strokeWidth={2} /> : i + 1}
                  </span>
                  <span className="hidden sm:inline">{labels[s]}</span>
                </div>
                {i < STEPS.length - 1 && <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" strokeWidth={2} />}
              </div>
            );
          })}
        </nav>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
            {step === "upload" && (
              <UploadStep onParsed={handleParsed} sectors={sectors} sector={sector} onSectorChange={setSector} periodMonths={periodMonths} onPeriodMonthsChange={setPeriodMonths} />
            )}
            {step === "mapping" && (
              <MappingStep items={rawItems} priorYears={priorYears} categories={categories} sector={sector} periodMonths={periodMonths} onBack={() => setStep("upload")} onConfirmed={handleMappingConfirmed} />
            )}
            {step === "results" && results && (
              <ResultsStep classifiedItems={classifiedItems} results={results} sector={sector} onRestart={restart} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      <Footer />
    </div>
  );
}
